import { t, useLocale } from "@/i18n/locale";
import { Message } from "@/i18n/Message";
// OAuth auth-callback route — re-fetches session and redirects.
import { useEffect, useState } from "react";
import type { AccountAuthState } from "../../auth/accountTypes";
import { fetchAccountSession } from "../../auth/accountSession";
import { consumeAccountNext, initiateSignIn } from "../../auth/accountCallback";
import {
  continuationCallbackParams,
  postContinuationCallback,
} from "../../auth/sessionContinuationChannel";
import {
  clearSaveAppIntent,
  consumeSaveAppIntent,
} from "../../auth/postAuthIntent";
import { touchLibraryApp, enrichLibraryApp } from "../../api/libraryClient";

function oauthErrorMessage(value: string): string {
  const normalized = value.toLowerCase();
  if (normalized.includes("access_denied"))
    return t("GitHub authentication was cancelled.");
  if (
    normalized.includes("signup_disabled") ||
    normalized.includes("admission")
  ) {
    return t("Your account does not currently have access to this environment.");
  }
  if (normalized.includes("account_not_linked")) {
    return t("This GitHub account could not be linked automatically.");
  }
  return t("GitHub sign-in could not be completed.");
}

async function resolveSessionAfterOAuth() {
  for (const delay of [0, 150, 400]) {
    if (delay)
      await new Promise<void>((resolve) => window.setTimeout(resolve, delay));
    const session = await fetchAccountSession();
    if (session) return session;
  }
  return null;
}

export default function AuthCallback({
  onSessionResolved,
  navigate,
}: {
  onSessionResolved: (state: AccountAuthState) => void;
  navigate: (route: string) => void;
}) {
  useLocale();
  const [error, setError] = useState<string | null>(null);
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const callbackError =
          new URLSearchParams(window.location.search).get(
            "error_description",
          ) ?? new URLSearchParams(window.location.search).get("error");
        if (callbackError) {
          setError(oauthErrorMessage(callbackError));
          onSessionResolved({ status: "signed_out" });
          return;
        }
        const session = await resolveSessionAfterOAuth();
        if (session) {
          onSessionResolved({ status: "signed_in", session });
          const continuation = continuationCallbackParams();
          if (continuation) {
            // "Continue this session" is a DISTINCT intent from "Save to My
            // Apps" (ato-api#267 / RFC #261 §4): a continue-preview sign-in
            // must NEVER also sweep an app into My Apps. Defensively drop any
            // stale save-app intent so the two can never both fire.
            clearSaveAppIntent();
            // Stale OAuth links from an older client must never start Checkout
            // or report a paid continuation as completed.
            if (continuation.option === "cloud_subscription") {
              navigate("/settings/billing/plans?waitlist=pro");
              return;
            }
            postContinuationCallback(
              continuation.channelId,
              continuation.option,
            );
            setComplete(true);
            window.setTimeout(() => window.close(), 0);
            return;
          }
          // "Save to My Apps" post-auth intent (ato-api#267): the user
          // explicitly chose to save ONE previewed capsule and signed in to do
          // it. Save exactly that capsule — never the whole preview history.
          // A plain sign-in (no intent) saves nothing, so signing in for any
          // other reason never sweeps previewed apps into My Apps (close
          // condition 1). This never touches runs.user_id (close condition 2).
          const saveApp = consumeSaveAppIntent();
          if (saveApp) {
            touchLibraryApp(saveApp);
            // Enrich with the capsule's name/icon so it doesn't sit as a bare
            // slug until the next launch (parity with GetRun's own save path).
            void enrichLibraryApp(saveApp);
          }
          // consumeAccountNext returns a sanitized internal path + search (no
          // hash, no credentials). replaceState to it and let popstate re-sync
          // the router — a stable path like /a/<slug>?autostart=1 or
          // /studio/capsules/<id>?step=build survives the round trip intact.
          const nextPath = consumeAccountNext();
          history.replaceState(null, "", nextPath);
          window.dispatchEvent(new PopStateEvent("popstate"));
        } else {
          setError(t("Sign-in did not complete. Please try again."));
          onSessionResolved({ status: "signed_out" });
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        onSessionResolved({ status: "error", message: msg });
      }
    })();
    // Run once on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (complete) {
    return (
      <div className="empty-state">
        <Message message={"Sign-in complete. Return to the game tab."} /><button className="settings-btn" onClick={() => window.close()}><Message message={"Close this tab"} /></button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-state">
        <p><Message message={"Sign-in failed: {{value0}}"} values={{ value0: error }} /></p>
        <button className="settings-btn" onClick={() => navigate("/settings")}><Message message={"Back to Settings"} /></button>
        <button className="settings-btn" onClick={() => void initiateSignIn()}><Message message={"Retry sign-in"} /></button>
      </div>
    );
  }

  return <div className="empty-state"><Message message={"Completing sign-in…"} /></div>;
}
